import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Subject } from 'rxjs';
import { DialogActionsEnum, IDialogTrigger } from '../../../dialog/dialog.data';
import { DialogService } from '../../../dialog/dialog.service';
import { Document } from '../../../document-data/document.model';
import { PaymentTransaction } from '../../../document-data/sub-models/payment-transaction.model';
import { BannerCanvasTypesEnum } from '../../../features-data/features-data.data';
import { FeaturesDataStore } from '../../../features-data/features-data.store';
import { Toggles } from '../../../features-data/sub-models/toggles.model';
import {
  ISendReceipt,
  ISendReceiptPreview,
} from '../../../payments-data/payments-data.data';
import { PaymentsSettings } from '../../../settings-data/sub-models/company-settings/payments.model';
import { I2gNgTrackingActionBuilder } from 'i2g-ng-tracking';
import { TrackingObjectBuilder } from '../../../tracking/tracking-object.builder';
import {
  ITrackingAction,
  ITrackingObject,
  TrackingDialogNamesEnum,
  TrackingScreenNamesEnum,
} from '../../../tracking/tracking.data';
import { SendReceiptDialogBuilder } from './send-receipt-dialog.builder';
import { ToasterDispatcher } from '../../../toaster/toaster.dispatcher';
import { filter, takeUntil } from 'rxjs/operators';

export const DIALOG_TYPE = 'send-receipt';

export interface ISendReceiptDialogData {
  transaction: PaymentTransaction;
  paymentsSettings: PaymentsSettings;
  document: Document;
}

@Component({
  selector: 'app-send-receipt-dialog',
  templateUrl: './send-receipt-dialog.component.html',
  styleUrls: ['./send-receipt-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [SendReceiptDialogBuilder],
})
export class SendReceiptDialogComponent implements OnInit, OnDestroy {
  @Input() trackingScreenName: TrackingScreenNamesEnum;

  @Output()
  onSendReceipt: EventEmitter<ISendReceipt> = new EventEmitter<ISendReceipt>();
  @Output()
  onPreviewReceipt: EventEmitter<ISendReceiptPreview> = new EventEmitter<ISendReceiptPreview>();
  @Output() onCancel: EventEmitter<void> = new EventEmitter<void>();

  private _destroyed: Subject<boolean> = new Subject<boolean>();
  private _transaction: PaymentTransaction;
  private _paymentsSettings: PaymentsSettings;
  private _document: Document;

  dialogType: string;
  description: string;
  trackingObject: ITrackingObject;
  sendReceiptTapped: ITrackingAction;
  sendPreviewTapped: ITrackingAction;
  cancelTapped: ITrackingAction;

  constructor(
    private _dialogService: DialogService,
    private _sendReceiptDialogBuilder: SendReceiptDialogBuilder,
    private _featuresDataStore: FeaturesDataStore,
    private _toasterDispatcher: ToasterDispatcher,
    private _changeDetectorRef: ChangeDetectorRef,
    private _trackingObjectBuilder: TrackingObjectBuilder,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder
  ) {}

  ngOnInit() {
    const dialogTriggers$ = this._dialogService.dialogTriggers$.pipe(
      filter(
        (dialogTrigger: IDialogTrigger) =>
          dialogTrigger.type === this.dialogType
      ),
      takeUntil(this._destroyed)
    );

    this.dialogType = DIALOG_TYPE;
    this.trackingObject = this._trackingObjectBuilder.buildDialog(
      TrackingDialogNamesEnum.send_receipt
    );
    this.sendReceiptTapped = this._trackingActionBuilder.buildButtonTapped(
      'send_receipt',
      this.trackingScreenName
    );
    this.sendPreviewTapped = this._trackingActionBuilder.buildButtonTapped(
      'send_preview',
      this.trackingScreenName
    );
    this.cancelTapped = this._trackingActionBuilder.buildButtonTapped(
      'cancel',
      this.trackingScreenName
    );

    dialogTriggers$
      .pipe(
        filter(
          (dialogTrigger: IDialogTrigger) =>
            dialogTrigger.action === DialogActionsEnum.refresh
        )
      )
      .subscribe(() => this._changeDetectorRef.markForCheck());

    dialogTriggers$
      .pipe(
        filter(
          (dialogTrigger: IDialogTrigger) =>
            dialogTrigger.action === DialogActionsEnum.open
        )
      )
      .subscribe((dialogTrigger: IDialogTrigger) => {
        const dialogData: ISendReceiptDialogData = dialogTrigger.data;

        this._document = dialogData.document;
        this._transaction = dialogData.transaction;
        this._paymentsSettings = dialogData.paymentsSettings;

        this.description = this._sendReceiptDialogBuilder.buildDescription(
          this._document.billing
        );

        this._changeDetectorRef.markForCheck();
      });
  }

  sendReceipt() {
    this._featuresDataStore
      .getFeatureToggles()
      .pipe(takeUntil(this._destroyed))
      .subscribe((featureToggles: Toggles) => {
        if (!featureToggles.isAllAccess(Toggles.KEYS.PAYMENT_RECEIPTS)) {
          this._dialogService.openGrowthDialog(
            BannerCanvasTypesEnum.feature_lock,
            Toggles.KEYS.PAYMENT_RECEIPTS
          );
        } else {
          const renderingOptions = this._sendReceiptDialogBuilder.buildRenderingOptions(
            this._paymentsSettings,
            this._document,
            featureToggles.isAllAccess(Toggles.KEYS.STRIPE_ACH_DEBIT),
            featureToggles.isAllAccess(Toggles.KEYS.CARD_PAYMENTS),
            featureToggles.isAllAccess(Toggles.KEYS.PAYPAL_EC)
          );
          const sendReceipt = this._sendReceiptDialogBuilder.buildSendReceipt(
            this._document,
            this._transaction,
            renderingOptions
          );

          this.onSendReceipt.emit(sendReceipt);
        }
      });
  }

  previewReceipt() {
    this._featuresDataStore
      .getFeatureToggles()
      .pipe(takeUntil(this._destroyed))
      .subscribe((featureToggles: Toggles) => {
        const renderingOptions = this._sendReceiptDialogBuilder.buildRenderingOptions(
          this._paymentsSettings,
          this._document,
          featureToggles.isAllAccess(Toggles.KEYS.STRIPE_ACH_DEBIT),
          featureToggles.isAllAccess(Toggles.KEYS.CARD_PAYMENTS),
          featureToggles.isAllAccess(Toggles.KEYS.PAYPAL_EC)
        );
        const payload = this._sendReceiptDialogBuilder.buildSendReceiptPreview(
          this.dialogType,
          this._transaction,
          renderingOptions,
          this._document
        );

        this.onPreviewReceipt.emit(payload);
      });
  }

  clearToast() {
    this._toasterDispatcher.clearToast(null);
  }

  ngOnDestroy() {
    this._dialogService.closeGrowthDialog(
      BannerCanvasTypesEnum.feature_lock,
      Toggles.KEYS.PAYMENT_RECEIPTS
    );
  }
}
